import React, { useCallback, useEffect, useState } from "react";

import Badge from "@cloudscape-design/components/badge";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Pagination from "@cloudscape-design/components/pagination";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";
import Toggle from "@cloudscape-design/components/toggle";

import { loadDepositLadderBannerOptions } from "../lib/depositLadderBannerOptions";
import { MAX_BANNER_BYTES } from "../stores/homeBannersAddModal";

const typeOptions = ["local", "international", "mixed"].map((value) => ({ value, label: value }));
const emptyPresentation = (values = {}) => ({
	image: "",
	title: "",
	titleType: "local",
	accent: "",
	accentType: "local",
	description: "",
	descriptionType: "local",
	...values,
});
const emptyForm = () => ({
	id: null,
	name: "",
	depositLadderId: null,
	isDefault: false,
	active: false,
	desktop: emptyPresentation(),
	mobile: emptyPresentation({ enabled: true }),
});

const PresentationFields = ({ Interface, form, platform, setForm }) => {
	const presentation = form[platform];
	const patch = (values) => setForm((current) => ({ ...current, [platform]: { ...current[platform], ...values } }));
	const typeSelect = (field) => (
		<Select
			selectedOption={typeOptions.find((option) => option.value === presentation[field])}
			options={typeOptions}
			onChange={({ detail }) => patch({ [field]: detail.selectedOption.value })}
		/>
	);

	return (
		<SpaceBetween direction="vertical" size="m">
			{platform === "mobile" && (
				<Toggle checked={presentation.enabled !== false} onChange={({ detail }) => patch({ enabled: detail.checked })}>
					{presentation.enabled !== false ? "Mobile banner enabled" : "Mobile banner disabled"}
				</Toggle>
			)}
			<FormField label={`${platform === "desktop" ? "Desktop" : "Mobile"} PNG image (max 1 MB)`}>
				<input
					type="file"
					accept="image/png"
					onChange={(event) => {
						const file = event.target.files[0];
						if (!file) return;
						if (file.size > MAX_BANNER_BYTES) {
							Interface.addNoty("Error", "Image size must not exceed 1 MB");
							event.target.value = "";
							return;
						}
						const reader = new FileReader();
						reader.onload = ({ target }) => patch({ image: target.result });
						reader.readAsDataURL(file);
					}}
				/>
				{presentation.image && (
					<Box margin={{ top: "s" }}>
						<img src={presentation.image} alt={`${platform} auth banner`} style={{ maxWidth: "100%", maxHeight: platform === "mobile" ? "120px" : "260px" }} />
					</Box>
				)}
			</FormField>
			<FormField label="Title type">{typeSelect("titleType")}</FormField>
			<FormField label="Title">
				<Input value={presentation.title} onChange={({ detail }) => patch({ title: detail.value })} />
			</FormField>
			<FormField label="Accent type">{typeSelect("accentType")}</FormField>
			<FormField label="Accent">
				<Input value={presentation.accent} onChange={({ detail }) => patch({ accent: detail.value })} />
			</FormField>
			<FormField label="Description type">{typeSelect("descriptionType")}</FormField>
			<FormField label="Description">
				<Input value={presentation.description} onChange={({ detail }) => patch({ description: detail.value })} />
			</FormField>
		</SpaceBetween>
	);
};

export default function AuthBanners({ Interface }) {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [pagesCount, setPagesCount] = useState(1);
	const [total, setTotal] = useState(0);
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [sorting, setSorting] = useState({ column: "id", order: "DESC" });
	const [modalVisible, setModalVisible] = useState(false);
	const [saving, setSaving] = useState(false);
	const [form, setForm] = useState(emptyForm());
	const [ladders, setLadders] = useState([]);
	const [laddersLoading, setLaddersLoading] = useState(false);

	const load = useCallback(() => {
		setLoading(true);
		Interface.request("get", { class: "data", module: "homeBanners", method: "listAuthBanners" }, { page, limit: 15, search, sorting })
			.then((result) => {
				setItems(result.data || []);
				setPagesCount(result.totalPages || 1);
				setTotal(result.total || 0);
			})
			.catch((error) => Interface.addNoty("Error", error?.text || error.toString()))
			.finally(() => setLoading(false));
	}, [Interface, page, search, sorting]);

	useEffect(() => load(), [load]);

	const applySearch = () => {
		setPage(1);
		setSearch(searchInput.trim());
	};

	const openModal = (item = null) => {
		setForm(item ? { ...item, desktop: { ...item.desktop }, mobile: { ...item.mobile } } : emptyForm());
		setModalVisible(true);
		setLaddersLoading(true);
		loadDepositLadderBannerOptions(Interface)
			.then(setLadders)
			.catch((error) => Interface.addNoty("Error", error?.text || error.toString()))
			.finally(() => setLaddersLoading(false));
	};

	const closeModal = () => {
		if (saving) return;
		setModalVisible(false);
		setForm(emptyForm());
	};

	const save = () => {
		setSaving(true);
		Interface.request("set", { class: "data", module: "homeBanners", method: form.id ? "editAuthBanner" : "addAuthBanner" }, form)
			.then(() => {
				Interface.addNoty("Success", `Auth banner was ${form.id ? "updated" : "created"}`);
				setModalVisible(false);
				setForm(emptyForm());
				load();
			})
			.catch((error) => Interface.addNoty("Error", error?.text || error.toString()))
			.finally(() => setSaving(false));
	};

	const ladderOptions = [
		{ value: null, label: "None" },
		...ladders.map((ladder) => ({ value: ladder.id, label: ladder.name, description: `ID: ${ladder.id} | ${ladder.mode} | ${Number(ladder.active) ? "Enabled" : "Disabled"}` })),
	];

	return (
		<>
			<Table
				trackBy="id"
				loading={loading}
				items={items}
				sortingColumn={{ sortingField: sorting.column }}
				sortingDescending={sorting.order === "DESC"}
				onSortingChange={({ detail }) => setSorting({ column: detail.sortingColumn.sortingField, order: detail.isDescending ? "DESC" : "ASC" })}
				columnDefinitions={[
					{ id: "id", header: "ID", sortingField: "id", cell: (item) => <Button variant="link" onClick={() => openModal(item)}>{item.id}</Button> },
					{ id: "name", header: "Name", sortingField: "name", cell: (item) => item.name },
					{ id: "desktop", header: "Desktop", cell: (item) => item.desktop.image ? <img src={item.desktop.image} alt="Desktop preview" style={{ width: "80px", maxHeight: "60px", objectFit: "cover" }} /> : "—" },
					{ id: "mobile", header: "Mobile", cell: (item) => item.mobile.enabled === false ? <Badge color="grey">Disabled</Badge> : item.mobile.image ? <img src={item.mobile.image} alt="Mobile preview" style={{ width: "120px", height: "32px", objectFit: "cover" }} /> : "—" },
					{ id: "depositLadderId", header: "Deposit ladder", sortingField: "depositLadderId", cell: (item) => item.isDefault ? "—" : item.depositLadderName || (item.depositLadderId ? `#${item.depositLadderId}` : "—") },
					{ id: "isDefault", header: "Default", sortingField: "isDefault", cell: (item) => item.isDefault ? <Badge color="blue">Default</Badge> : "—" },
					{ id: "active", header: "Active", sortingField: "active", cell: (item) => <Badge color={item.active ? "green" : "grey"}>{item.active ? "Active" : "Inactive"}</Badge> },
					{ id: "updatedAt", header: "Updated", sortingField: "updatedAt", cell: (item) => item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "—" },
				]}
				filter={
					<SpaceBetween direction="horizontal" size="xs">
						<Input
							value={searchInput}
							inputMode="search"
							type="search"
							placeholder="Search by name"
							onChange={({ detail }) => setSearchInput(detail.value)}
							onKeyDown={({ detail }) => {
								if (detail.key === "Enter") applySearch();
							}}
						/>
						<Button iconName="search" onClick={applySearch}>Search</Button>
					</SpaceBetween>
				}
				header={<Header counter={`(${total})`} actions={<Button variant="primary" onClick={() => openModal()}>Add Auth Banner</Button>}>Auth Banners</Header>}
				pagination={<Pagination currentPageIndex={page} pagesCount={pagesCount} onChange={({ detail }) => setPage(detail.currentPageIndex)} />}
				empty="No Auth Banners found"
			/>

			<Modal
				visible={modalVisible}
				size="large"
				header={form.id ? `Edit Auth Banner #${form.id}` : "Add Auth Banner"}
				onDismiss={closeModal}
				footer={<Box float="right"><SpaceBetween direction="horizontal" size="s"><Button onClick={closeModal}>Close</Button><Button variant="primary" loading={saving} onClick={save}>Save</Button></SpaceBetween></Box>}>
				<Tabs tabs={[
					{
						id: "general",
						label: "General",
						content: <SpaceBetween direction="vertical" size="m">
							<FormField label="Name"><Input value={form.name} onChange={({ detail }) => setForm((current) => ({ ...current, name: detail.value }))} /></FormField>
							<Toggle checked={form.isDefault} disabled={Boolean(form.id && form.isDefault)} onChange={({ detail }) => setForm((current) => ({ ...current, isDefault: detail.checked, active: detail.checked || current.active, depositLadderId: detail.checked ? null : current.depositLadderId }))}>{form.isDefault ? "Default banner" : "Regular banner"}</Toggle>
							<Toggle checked={form.active} disabled={form.isDefault} onChange={({ detail }) => setForm((current) => ({ ...current, active: detail.checked }))}>{form.active ? "Active" : "Inactive"}</Toggle>
							<FormField label="Deposit ladder">
								<Select disabled={form.isDefault} filteringType="auto" statusType={laddersLoading ? "loading" : "finished"} options={ladderOptions} selectedOption={ladderOptions.find((option) => Number(option.value) === Number(form.depositLadderId)) || ladderOptions[0]} onChange={({ detail }) => setForm((current) => ({ ...current, depositLadderId: detail.selectedOption.value }))} />
							</FormField>
						</SpaceBetween>,
					},
					{ id: "desktop", label: "Desktop", content: <PresentationFields Interface={Interface} form={form} platform="desktop" setForm={setForm} /> },
					{ id: "mobile", label: "Mobile", content: <PresentationFields Interface={Interface} form={form} platform="mobile" setForm={setForm} /> },
				]} />
			</Modal>
		</>
	);
}
